import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Box,
  Chip,
  Alert,
  Button,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  getActionLabel,
  getSubjectLabel,
  ABILITY_ACTIONS,
  ABILITY_SUBJECTS,
} from '../../../server/abilityHelper'

interface Permission {
  action: string
  subject: string
  inverted?: boolean
}

interface PermissionListProps {
  permissions: Permission[]
  onRemovePermission: (index: number) => void
  onClearAll?: () => void
  emptyMessage?: string
  showIndices?: boolean
  isMobile?: boolean
}

export function PermissionList({
  permissions,
  onRemovePermission,
  onClearAll,
  emptyMessage = 'Henüz izin tanımlanmamış',
  showIndices = false,
  isMobile = false,
}: PermissionListProps) {
  if (permissions.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info" sx={{ justifyContent: 'center' }}>
          {emptyMessage}
        </Alert>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        height: isMobile ? 'auto' : '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {onClearAll && permissions.length > 0 && (
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={onClearAll}
            fullWidth
          >
            Tümünü Sil
          </Button>
        </Box>
      )}
      <List
        sx={{
          flex: 1,
          height: isMobile ? 'auto' : '100%',
          maxHeight: isMobile ? '400px' : '100%',
          overflow: 'auto',
          m: 0,
          p: 0,
          '& .MuiListItem-root': {
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-child': {
              borderBottom: 'none',
            },
          },
        }}
      >
        {permissions.map((permission, index) => (
          <ListItem key={index} sx={{ py: 1.5, px: 2 }}>
            <ListItemText
              primary={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {showIndices && (
                    <Typography
                      variant="caption"
                      sx={{
                        bgcolor: 'grey.100',
                        color: 'grey.600',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontWeight: 'medium',
                        minWidth: '24px',
                        textAlign: 'center',
                      }}
                    >
                      {index + 1}
                    </Typography>
                  )}
                  <Chip
                    label={getActionLabel(
                      permission.action as keyof typeof ABILITY_ACTIONS,
                    )}
                    size="small"
                    color={permission.inverted ? 'error' : 'primary'}
                    variant="outlined"
                  />
                  <Chip
                    label={getSubjectLabel(
                      permission.subject as keyof typeof ABILITY_SUBJECTS,
                    )}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                  {permission.inverted && (
                    <Chip
                      label="⚠️ Yasaklama"
                      size="small"
                      color="warning"
                      variant="filled"
                    />
                  )}
                </Box>
              }
              secondary={
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {permission.inverted
                    ? 'Bu işlem yasaklanmış'
                    : 'Bu işlem izin veriliyor'}
                </Typography>
              }
            />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                aria-label="sil"
                onClick={() => onRemovePermission(index)}
                size="small"
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Box>
  )
}
